const { default: Decimal } = require('decimal.js');

const { rounding } = require('./rounding');

/**
 * Retrieves the markup percentage for a stock item based on its delivery category and channel.
 * The markup is determined in the following order of precedence:
 * 1. Item-specific markup (if exists)
 * 2. Category markup (if item has a delivery category and category markup exists)
 * 3. Default channel markup (fallback)
 *
 * @param {Object} stockItem - The item object containing delivery categories and markup information
 * @param {Object} config - Configuration object containing markup defaults and overrides
 * @param {string} channelName - The name of the delivery channel ('deliveroo', 'uber-eats', 'just-eat')
 *
 * @returns {number} The markup percentage to be applied
 */
const getMarkupPercentage = (stockItem, config, channelName) => {
	let markup;
	const mainDeliveryCategory = stockItem.deliveryCategories?.[0];

	const itemMarkup = stockItem.deliveryMarkup?.[channelName];
	const categoryMarkup = config.categoryOverrides?.[mainDeliveryCategory]?.deliveryMarkup;
	const channelMarkup = config.defaultDeliveryMarkup || 0;

	if (itemMarkup !== null && itemMarkup !== undefined) {
		markup = itemMarkup;
	} else if (mainDeliveryCategory && categoryMarkup !== null && categoryMarkup !== undefined) {
		markup = categoryMarkup;
	} else {
		markup = channelMarkup;
	}

	return markup;
};

/**
 * Calculates the final price for an item by applying appropriate markup,
 * and rounding it according to the specified rounding rules.
 *
 * @param {Object} stockItem - The item object containing price and markup information
 * @param {Object} config - Configuration object containing markup defaults and overrides
 * @param {string} channelName - The name of the delivery channel ('deliveroo', 'uber-eats', 'just-eat')
 * @param {boolean} toCents - Flag to indicate if the final price should be returned in cents instead of decimal (default: false)
 *
 * @returns {number} The final calculated price with markup applied
 */
const getPrice = (stockItem, config, channelName, toCents = false) => {
	const markup = getMarkupPercentage(stockItem, config, channelName);

	const markedUpPrice = new Decimal(stockItem.price)
		.mul(1 + markup / 100)
		.toDecimalPlaces(2)
		.toNumber();

	const roundedPrice = rounding(markedUpPrice, config.priceRounding);

	if (toCents) {
		return new Decimal(roundedPrice).mul(100).toNumber();
	}

	return roundedPrice;
};

/**
 * Calculates the final price for an organisation stock item by applying appropriate markup
 * to the maximum price from the price range, and rounding it according to the specified rounding rules.
 * This is specifically designed for organisation stock items which have priceRange instead of a single price.
 *
 * @param {Object} orgStockItem - The organisation stock item containing priceRange and markup information
 * @param {Object} config - Configuration object containing markup defaults and overrides
 * @param {string} channelName - The name of the delivery channel ('deliveroo', 'uber-eats', 'just-eat')
 * @param {boolean} toCents - Flag to indicate if the final price should be returned in cents instead of decimal (default: false)
 *
 * @returns {number} The final calculated price with markup applied, based on the maximum price
 */
const getMaxPrice = (orgStockItem, config, channelName, toCents = false) => {
	const markup = getMarkupPercentage(orgStockItem, config, channelName);

	const maxPrice = orgStockItem.priceRange?.max || 0;

	const markedUpPrice = new Decimal(maxPrice)
		.mul(1 + markup / 100)
		.toDecimalPlaces(2)
		.toNumber();

	const roundedPrice = rounding(markedUpPrice, config.priceRounding);

	if (toCents) {
		return new Decimal(roundedPrice).mul(100).toNumber();
	}

	return roundedPrice;
};

module.exports = { getPrice, getMaxPrice, getMarkupPercentage };
